import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, CheckCircle2, FileText, Zap } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Notification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  severity: string;
  read_at: string | null;
  created_at: string;
}

const iconMap: Record<string, typeof AlertTriangle> = {
  'finding.high': AlertTriangle,
  'mutation.approval_needed': Zap,
  'report.ready': FileText,
};

const colorMap: Record<string, string> = {
  warning: 'text-amber-500',
  error: 'text-red-500',
  success: 'text-emerald-500',
  info: 'text-blue-500',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  // Lightweight unread-count poll (shared key with AlertsPage + sidebar badge).
  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => (await api.get('/notifications/unread-count')).data as { count: number },
    refetchInterval: 30_000,
  });

  // Full list only loaded when the dropdown is open — saves bandwidth.
  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications?per_page=5')).data.data ?? [],
    refetchInterval: 60_000,
    enabled: open,
  });

  const unread = countData?.count ?? notifications.filter((n) => !n.read_at).length;

  // Zamknij dropdown na klik poza
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto card shadow-popover z-50 fade-in">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <p className="text-sm font-semibold">Powiadomienia</p>
            {unread > 0 && (
              <button
                onClick={async () => {
                  await api.post('/notifications/mark-all-read');
                  qc.invalidateQueries({ queryKey: ['notifications'] });
                  qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
                  qc.invalidateQueries({ queryKey: ['alerts'] });
                }}
                className="text-xs text-brand-600 hover:underline"
              >
                Oznacz wszystkie
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">
              Brak powiadomień
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {notifications.slice(0, 5).map((n) => {
                const Icon = iconMap[n.type] ?? CheckCircle2;
                return (
                  <div
                    key={n.id}
                    className={cn(
                      'p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer',
                      !n.read_at && 'bg-blue-50/50 dark:bg-blue-900/10',
                    )}
                  >
                    <div className="flex gap-2.5">
                      <Icon size={16} className={cn('mt-0.5 shrink-0', colorMap[n.severity] ?? 'text-slate-400')} />
                      <div className="min-w-0">
                        <p className={cn('text-sm', !n.read_at && 'font-medium')}>{n.title}</p>
                        {n.body && <p className="text-xs text-slate-500 mt-0.5 truncate">{n.body}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">
                          {new Date(n.created_at).toLocaleString('pl-PL')}
                        </p>
                      </div>
                      {!n.read_at && <span className="h-2 w-2 rounded-full bg-brand-500 shrink-0 mt-1.5" />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="p-2 border-t border-slate-200 dark:border-slate-700 text-center">
            <Link
              to="/alerts"
              onClick={() => setOpen(false)}
              className="text-xs text-brand-600 hover:underline"
            >
              Zobacz wszystkie
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
